import React, { useMemo } from 'react';
import { CreditCard, AlertTriangle, Clock3, CheckCircle2 } from 'lucide-react';
import { MetricGrid, SectionHeader, StatCard, billingLabel, billingPillClass } from '../shared';
import type { SuperAdminContext } from '../types';

export function BillingSection({ ctx }: { ctx: SuperAdminContext }) {
  const { analytics, activeOrgs, accessOrgs } = ctx;

  const buckets = useMemo(() => {
    const active = activeOrgs;
    const pastDue = accessOrgs.filter((o) => o.subscriptionStatus === 'past_due');
    const held = accessOrgs.filter((o) => o.accountHold);
    return { active, pastDue, held };
  }, [activeOrgs, accessOrgs]);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Billing & plans"
        subtitle="Active subscribers and collections risk. Pending checkout profiles appear only as a count — see Tenant registry for the full funnel."
      />

      <MetricGrid>
        <StatCard label="Active" value={analytics.activeBilling} tone="success" icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />} hint="Paying / active subscriptions" />
        <StatCard label="Pending checkout" value={analytics.pending} tone="warning" icon={<Clock3 className="h-4 w-4 text-amber-600" />} hint="Registered, payment not completed — not listed below" />
        <StatCard label="Past due" value={analytics.pastDue} tone="danger" icon={<AlertTriangle className="h-4 w-4 text-rose-600" />} hint="Collections priority" />
        <StatCard label="On hold" value={analytics.onHold} tone="danger" icon={<CreditCard className="h-4 w-4" />} hint="Access suspended" />
      </MetricGrid>

      <div className="grid gap-4 lg:grid-cols-2">
        <BillingList title="Past due tenants" items={buckets.pastDue} empty="No past-due tenants." tone="rose" />
        <BillingList title="Active subscribers" items={buckets.active} empty="No active tenants." tone="emerald" />
        <BillingList title="On access hold" items={buckets.held} empty="No holds placed." tone="rose" />
      </div>
    </div>
  );
}

function BillingList({
  title,
  items,
  empty,
  tone,
}: {
  title: string;
  items: SuperAdminContext['orgs'];
  empty: string;
  tone: 'rose' | 'amber' | 'emerald';
}) {
  const border =
    tone === 'rose' ? 'border-rose-200' : tone === 'amber' ? 'border-amber-200' : 'border-emerald-200';
  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${border}`}>
      <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
      <ul className="mt-3 space-y-2">
        {items.length === 0 ? (
          <li className="text-sm text-zinc-500">{empty}</li>
        ) : (
          items.map((o) => (
            <li key={o.id} className="flex items-center justify-between gap-2 rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm">
              <div className="min-w-0">
                <p className="truncate font-medium text-zinc-900">{o.name}</p>
                <p className="truncate text-xs text-zinc-500">{o.adminEmail}</p>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${billingPillClass(o.subscriptionStatus)}`}>
                {billingLabel(o.subscriptionStatus)}
              </span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
